/**
 * Sollae Cloud - Application Entry Point (main.js)
 * 솔래 클라우드 - 애플리케이션 진입점 (main.js)
 * 
 * English:
 * - What: The main file that starts the Vue app.
 * - Usage: This file sets up the Vue app and registers global components, tools (directives), and plugins.
 * - Feature Support: Important for starting the app and setting up global UI tools like Modals, Toasts, and Alarms.
 * - Why: It is needed to attach the Vue app to the web page and set up global parts.
 * - Linked Files: App.vue (root), router/index.js, store/index.js, locales/i18n.js, and js/plugin.js.
 * - Editing Guide:
 *   - To add new global plugins, use 'Vue.use(Plugin)'.
 *   - To register global components, use 'Vue.component()'.
 *   - To change how the app starts, update the 'new Vue()' part.
 * 
 * 한국어:
 * - 역할: Vue 애플리케이션의 메인 진입점입니다.
 * - 용도: 루트 Vue 인스턴스를 초기화하고 전역 컴포넌트, 디렉티브 및 플러그인을 등록합니다.
 * - 지원 기능: 앱 부트스트랩/초기화, 전역 UI 플러그인(모달, 토스트, 알람), 외부 클릭 감지 로직 등을 지원합니다.
 * - 필요성: Vue 앱을 DOM에 마운트하고 전역 의존성을 설정하기 위해 필수적입니다.
 * - 연결된 파일: App.vue (루트), router/index.js, store/index.js, locales/i18n.js, 그리고 js/plugin.js를 연결합니다.
 * - 수정 가이드:
 *   - 새로운 전역 플러그인이나 외부 라이브러리를 추가하려면 'Vue.use(Plugin)'을 사용하세요.
 *   - 전역 컴포넌트를 등록하려면 'Vue.component()'를 사용하세요.
 *   - 앱이 마운트되는 방식을 변경하려면 'new Vue()' 설정을 수정하세요.
 */

import Vue from 'vue';
import UserPage from '@/App'; // Main root component (루트 컴포넌트)
import router from '@/router'; // Routing configuration (라우팅 설정)
import store from '@/store'; // State management (상태 관리)
import i18n from '@/locales/i18n'; // Localization (다국어 지원)

// Register custom global plugins (전역 플러그인 등록)
// These provide global UI feedback elements like Modals and Toasts.
import { Modal, Toast, ProgressBar, AlarmSound, FooterMsg } from '@/js/plugin';
import LoaderView from '@/components/common/LoaderView';

// Global component for displaying loading states
Vue.component('loader-view', LoaderView);

// Custom Directive: v-click-outside
// Detects clicks outside of an element (e.g., for closing menus/modals)
// 엘리먼트 외부 클릭 감지용 디렉티브 (메뉴/모달 닫기 등에 사용)
const fn = {};
let fnIndex = 0;
Vue.directive('click-outside', {
  bind(el, binding, vNode) {
    if (typeof binding.value !== 'function') {
      const compName = vNode.context.name;
      let warn = `[click-outside:] provided expression '${binding.expression}' is not a function, but has to be`;
      if (compName) {
        warn += `Found in component '${compName}' `;
      }
      console.warn(warn);
    }

    const handler = function (e) {
      if (el.contains(e.target)) {
        e.stopPropagation();
      } else {
        binding.value(e);
      }
    };
    fn[fnIndex] = handler;
    el.setAttribute('data-outside-id', fnIndex);
    window.addEventListener('mousedown', handler);
    window.addEventListener('touchstart', handler, { passive: true });
    fnIndex++;
  },
  unbind(el, binding, vNode) {
    const index = el.getAttribute('data-outside-id');
    window.removeEventListener('mousedown', fn[index]);
    window.removeEventListener('touchstart', fn[index], { passive: true });
    fn[index] = null;
    delete fn[index];
  },
});

// Configure Vue production/performance settings
Vue.config.productionTip = false;
Vue.config.performance = true;

// Install Plugins
Vue.use(Modal);
Vue.use(Toast);
Vue.use(ProgressBar);
Vue.use(FooterMsg);
Vue.use(AlarmSound);

/* eslint-disable no-new */
/**
 * Create the root Vue instance
 * 루트 Vue 인스턴스 생성
 */
new Vue({
  el: '#main',
  router,
  i18n,
  store,
  components: { UserPage },
  template: '<UserPage/>',
});
